<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import { useRoute } from "vue-router";
import { formatGapTime } from '../utils/gap-formatter.js'
import { formatMarkerText } from '../utils/marker-formatter.js'

const resultsStore = useResultsStore()
const route = useRoute()
const eventAlias = route.params.eventAlias
resultsStore.fetchResults(eventAlias)

const now = ref(Date.now())
let refreshResultsTimer = setInterval(() => {
      resultsStore.fetchResults(route.params.eventAlias)
    }, 30000)
let timer = null

// Track recently changed rows by result.id and the timestamp of change
const recentlyChanged = ref({})
const superLive = ref(true)
const showElapsedTime = ref(true)

// Helper to compare results and mark changed rows
let lastResults = []

watch(
  () => resultsStore.results.map(r => ({ id: r.id, score: r.score, finish_time: r.finish_time, finish_time_gps: r.finish_time_gps, dnf: r.dnf, abs: r.abs, categoryPosition: r.categoryPosition })),
  (newResults) => {
    const nowTime = Date.now()
    
    // Compare with last results to find changes
    newResults.forEach(newResult => {
      const lastResult = lastResults.find(r => r.id === newResult.id)
      if (lastResult) {
        // Check if any relevant field changed
        if (
          lastResult.score !== newResult.score ||
          lastResult.finish_time !== newResult.finish_time ||
          lastResult.finish_time_gps !== newResult.finish_time_gps ||
          lastResult.dnf !== newResult.dnf ||
          lastResult.abs !== newResult.abs ||
          lastResult.categoryPosition !== newResult.categoryPosition
        ) {
          recentlyChanged.value[newResult.id] = nowTime
        }
      }
    })
    
    lastResults = [...newResults]
  },
  { deep: true }
)

onMounted(() => {
  timer = setInterval(() => {
    now.value = Date.now()
  }, 1000)

  const CLEANUP_INTERVAL = 5000; // 5 seconds
  setInterval(() => {
    // Clean up expired entries
    const cutoff = Date.now() - 30 * 1000; // 30 seconds
    for (const id in recentlyChanged.value) {
      if (recentlyChanged.value[id] < cutoff) {
        delete recentlyChanged.value[id]
      }
    }
  }, CLEANUP_INTERVAL)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
  if (refreshResultsTimer) clearInterval(refreshResultsTimer)
})

function getResultClass(result) {
  if (result.dnf) {
    return 'text-danger fw-bold'
  }
  if (result.abs) {
    return 'text-muted fst-italic'
  }
  return 'result-upcoming'
}

function calculateTimeFromStart(result) {
  const startTime = new Date(result.start_time)
  const elapsed = new Date(now.value - startTime)
  return getHHMMSSString(elapsed)
}

function timeFromStart(result) {
  const startTime = new Date(result.start_time)
  const now = new Date()
  const elapsed = new Date(now - startTime)
  return elapsed
}

function timeFromStartToSplit(result, split) {
    const startTime = new Date(result.start_time)
    const splitTime = new Date(split.time)
    const elapsed = new Date(splitTime - startTime)
    return elapsed
}

function getHHMMSSString(date) {
  if (!date) return '--:--:--'
  
  // Handle negative or zero elapsed time - don't display
  const timestamp = date instanceof Date ? date.getTime() : (typeof date === 'number' ? date : 0)
  if (timestamp <= 0) {
    return ''
  }
  
  // Get hours, minutes, seconds from Date object
  let hours = date.getUTCHours()
  let minutes = date.getUTCMinutes()
  let seconds = date.getUTCSeconds()
  
  // Pad with zeros if needed
  hours = hours.toString().padStart(2, '0')
  minutes = minutes.toString().padStart(2, '0')
  seconds = seconds.toString().padStart(2, '0')
  
  return `${hours}:${minutes}:${seconds}`
}

// New functions for splits calculations
function calculateSplitTime(result, currentMarker, previousMarker) {
  if (!result.splits || !result.splits[currentMarker.id]) return null
  
  const currentSplitTime = new Date(result.splits[currentMarker.id].time)
  
  if (!previousMarker) {
    // First marker - calculate from start time
    if (!result.start_time) return null
    const startTime = new Date(result.start_time)
    return new Date(currentSplitTime - startTime)
  } else {
    // Calculate from previous marker
    if (!result.splits[previousMarker.id]) return null
    const previousSplitTime = new Date(result.splits[previousMarker.id].time)
    return new Date(currentSplitTime - previousSplitTime)
  }
}

function getPositionAtSplit(markerId) {
  if (!resultsStore.results) return {}
  
  // Get all results that have this split
  const resultsWithSplit = resultsStore.results
    .filter(r => r.splits && r.splits[markerId] && !r.dnf && !r.abs)
    .sort((a, b) => {
      const aTime = new Date(a.splits[markerId].time)
      const bTime = new Date(b.splits[markerId].time)
      return aTime - bTime
    })
  
  // Return a map of result.id -> position
  const positionMap = {}
  resultsWithSplit.forEach((result, index) => {
    positionMap[result.id] = index + 1
  })
  
  return positionMap
}

function getFastestSplitTimes(markerId, previousMarkerId) {
  if (!resultsStore.results) return new Set()
  
  // Get all split times for this segment
  const splitTimes = []
  
  resultsStore.results.forEach(result => {
    if (!result.splits || !result.splits[markerId] || result.dnf || result.abs) return
    
    const splitTime = calculateSplitTime(result, { id: markerId }, previousMarkerId ? { id: previousMarkerId } : null)
    if (splitTime) {
      splitTimes.push({
        resultId: result.id,
        time: splitTime.getTime()
      })
    }
  })
  
  // Sort by time and get the fastest
  splitTimes.sort((a, b) => a.time - b.time)
  
  if (splitTimes.length === 0) return new Set()
  
  const fastestTime = splitTimes[0].time
  return new Set(splitTimes.filter(st => st.time === fastestTime).map(st => st.resultId))
}

function getSplitNumber(markerIndex) {
  return markerIndex + 1
}

function getMarkersAsArray() {
  if (!resultsStore.markersMap) return []
  return Object.values(resultsStore.markersMap).sort((a, b) => a.sort - b.sort)
}

function sortedResults() {
    return resultsStore.results.slice().sort((a, b) => {
        if (a.abs) {
            return 1
        }
        if (b.abs) {
            return -1
        }
        if (a.dnf) {
            return 1
        }
        if (b.dnf) {
            return -1
        }
        if (a.finish_time || a.finish_time_gps || b.finish_time || b.finish_time_gps){
            if (a.finish_time || a.finish_time_gps) {
                return -1
            }
            if (b.finish_time || b.finish_time_gps) {
                return 1
            }
        } else {
            return compareElapsedTimeAtLastControl(a, b)
        }
    })
}

function compareElapsedTimeAtLastControl(a, b) {
    if (!a.lastMarker && !b.lastMarker) {
        return a.number - b.number
    }
    if (!a.lastMarker) {
        return 1
    }
    if (!b.lastMarker) {
        return -1
    }
    if (a.lastMarker.sort == b.lastMarker.sort) {
        let aDate = new Date(a.lastSplit.time.replace(' ', 'T') + 'Z')
        let bDate = new Date(b.lastSplit.time.replace(' ', 'T') + 'Z')
        if (aDate.getTime() == bDate.getTime()) {
            return a.number - b.number
        } else {
            return aDate.getTime() - bDate.getTime()
        }
    } else {
        if (a.lastMarker.sort > b.lastMarker.sort) {
            if (a.lastMarker.sort - 1 == b.lastMarker.sort) {
                if (a.splits[b.lastMarker.id]) {
                    if (a.splits[b.lastMarker.id].time > b.lastSplit.time) {
                        return timeFromStartToSplit(a, a.lastSplit) - timeFromStart(b)
                    } 
                }
            }
            return -1
        } else {
            if (b.lastMarker.sort - 1 == a.lastMarker.sort) {
                if (b.splits[a.lastMarker.id]) {
                    if (b.splits[a.lastMarker.id].time > a.lastSplit.time) {
                        return timeFromStart(a) - timeFromStartToSplit(b, b.lastSplit)
                    } 
                }
            }
            return 1
        }
    }
}
</script>

<template>
  <div>
    <h1>Wyniki z podziałami</h1>
    <div class="table-scroll">
        <div class="settings">
            <span class="badge bg-secondary">Event Alias: {{ eventAlias }}</span>
            <span class="badge bg-info">Aktualizacja co 30 sekund</span>
            <span class="badge bg-success">Czas: {{ new Date(now).toLocaleTimeString() }}</span>
            <!-- checkbox to switch between super live results -->
            <input type="checkbox" v-model="superLive" class="form-check-input" />
            <label class="form-check-label">Super Live</label>
            <!-- checkbox to switch between elapsed time and arrival time -->
            <input type="checkbox" v-model="showElapsedTime" class="form-check-input" />
            <label class="form-check-label">Czas narastająco</label>

        </div>
      <table class="table table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>Zawodnik</th>
            <th v-if="resultsStore.shouldShowCategoryColumn()">kat.</th>
            <th>Start</th>
            <th v-for="marker in getMarkersAsArray()" :key="marker.id" class="marker-column">
                <span v-html="formatMarkerText(marker.text)"></span>
            </th>
            <th>Czas</th>
            <th>Strata</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(result, index) in sortedResults()"
            :key="result.id"
            :class="{ 'row-changed': recentlyChanged[result.id] }"
          >
            <td>{{ index + 1 }}</td>
            <td><span class="badge bg-secondary">{{ result.number }}</span> {{ result.name }}</td>
            <td v-if="resultsStore.shouldShowCategoryColumn()">
              {{ result.categoryPosition }}
            </td>
            <td>{{ result.start_time ? new Date(result.start_time).toLocaleTimeString() : '' }}</td>
            <td v-for="(marker, markerIndex) in getMarkersAsArray()" :key="marker.id" class="marker-cell">
              <span v-if="markerIndex == getMarkersAsArray().length - 1">
                  {{ result.finish_time ? new Date(result.finish_time).toLocaleTimeString() : (result.finish_time_gps ? new Date(result.finish_time_gps).toLocaleTimeString() : '')}}
                  <span v-if="!result.finish_time && result.finish_time_gps" class="badge bg-warning">GPS</span>
              </span>
                <span v-else-if="result.splits[marker.id]"> 
                    <div class="split-cell">
                        <!-- Main time display -->
                        <div class="main-time">
                            <span v-if="showElapsedTime">
                                {{ result.splits[marker.id].time ? getHHMMSSString(timeFromStartToSplit(result, result.splits[marker.id])) : '--:--:--' }}
                            </span>
                            <span v-else>
                                {{ result.splits[marker.id].time ? new Date(result.splits[marker.id].time).toLocaleTimeString() : '--:--:--' }}
                            </span>
                            <span class="position-badge">({{ getPositionAtSplit(marker.id)[result.id] || '-' }})</span>
                        </div>
                        
                        <!-- Split time display -->
                        <div class="split-time" v-if="result.splits[marker.id]">
                            <span 
                                :class="{
                                    'fastest-split': getFastestSplitTimes(marker.id, markerIndex > 0 ? getMarkersAsArray()[markerIndex - 1].id : null).has(result.id)
                                }"
                            >
                                {{ calculateSplitTime(result, marker, markerIndex > 0 ? getMarkersAsArray()[markerIndex - 1] : null) ? getHHMMSSString(calculateSplitTime(result, marker, markerIndex > 0 ? getMarkersAsArray()[markerIndex - 1] : null)) : '--:--:--' }}
                            </span>
                            <span class="split-number">({{ getSplitNumber(markerIndex) }})</span>
                        </div>
                    </div>
                </span>
            </td>
            <td>
              <span v-if="result.dnf || result.abs" :class="getResultClass(result)">{{ result.abs? "DNS" : "DNF" }}</span>
              <span v-else :class="getResultClass(result)">
                {{ result.finish_time ? result.score : (result.finish_time_gps ? result.score : (result.start_time ? calculateTimeFromStart(result) : '--:--:--')) }}
              </span>
            </td>
            <td>
              {{ formatGapTime(resultsStore.getGapToLeader(result)) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.table {
    text-align: left;
}

/* Center-align marker column headers */
.marker-column {
    text-align: center;
}

/* Center-align marker column data cells */
.table td.marker-cell {
    text-align: center;
}
.result-upcoming {
  font-style: italic;
}
.table-scroll {
  width: 100%;
  max-height: 80vh;
  overflow-x: auto;
  overflow-y: auto;
}

/* Sticky table header for vertical scrolling */
.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa; /* Bootstrap table header background */
}

/* Sticky columns for horizontal scrolling */
.table th:nth-child(1),
.table td:nth-child(1) {
  position: sticky;
  left: 0;
  z-index: 10;
  background-color: white;
}

.table th:nth-child(2),
.table td:nth-child(2) {
  position: sticky;
  left: 60px; /* Adjust based on first column width */
  z-index: 10;
  background-color: white;
}

/* Ensure header has higher z-index and combines both sticky behaviors */
.table thead th:nth-child(1),
.table thead th:nth-child(2) {
  z-index: 12; /* Higher than both horizontal sticky columns and vertical sticky header */
  background-color: #f8f9fa; /* Bootstrap table header background */
}

/* Ensure sticky columns maintain background even when row is highlighted */
.table tbody tr.row-changed td:nth-child(1),
.table tbody tr.row-changed td:nth-child(2) {
  background-color: #ebd0b2 !important; /* Keep highlight color for sticky columns */
}
.table.table-striped > tbody > tr.row-changed > td,
tbody tr.row-changed > td {
  background: #ebd0b2 !important;
  transition: background 1s;
  font-weight: bold !important;
  animation: highlight-row 1s linear;
}
@keyframes highlight-row {
  0% { background: #ffa754; }
  100% { background: #ebd0b2; }
}

.split-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.main-time {
  font-weight: bold;
}

.position-badge {
  font-size: 0.8em;
  color: #666;
  margin-left: 4px;
}

.split-time {
  font-size: 0.9em;
  color: #555;
}

.split-number {
  font-size: 0.75em;
  color: #999;
  margin-left: 3px;
}

.fastest-split {
  background-color: #ffeb3b;
  padding: 1px 3px;
  border-radius: 3px;
  font-weight: bold;
}

.settings {
  margin-bottom: 10px;
}

.settings .badge {
  margin-right: 10px;
}

.settings input[type="checkbox"] {
  margin-right: 5px;
  margin-left: 15px;
}

.settings label {
  margin-right: 10px;
}
</style>